import { APP_ENV } from '../../../legacy/researcher/app-constants';
import { CookieConsentUtils } from '../../../legacy/researcher/modules/common/modules/cookie/utils/cookie-consent.utils';
import { cookieConsent, legacyCookieConsent } from '../../../legacy/researcher/modules/common/modules/cookie/utils/cookie-consent.strategy';
import { Environment } from '../../../../lib/helpers/enums';

class CookieService {
  /**
   * @description
   * Get the user consent preferences directly from the cookie, which
   * is stored as Google Tag Manager preferences.
   *
   * @returns {ConsentPreferences | null}
   */
  getConsent() {
    return cookieConsent.get() ?? null;
  }

  /**
   * @description
   * Get the consent properties for accepting all.
   *
   * @returns {ConsentProperties}
   */
  getAcceptAll() {
    this.setConsent(CookieConsentUtils.getAcceptAll());
  }

  /**
   * @description
   * Get the consent properties for declining all.
   *
   * @returns {ConsentProperties}
   */
  getDeclineAll() {
    this.setConsent(CookieConsentUtils.getDeclineAll());
  }

  /**
   * @description
   * Get the default consent properties and convert to Google Tag
   * Manager preferences.
   *
   * @returns {ConsentPreferences}
   */
  getDefaultConsentPreferences() {
    const consentProperties = CookieConsentUtils.getDefaultConsent();
    return CookieConsentUtils.mapConsentPropertiesToPreferences(
      consentProperties
    );
  }

  /**
   * @description
   * Set the consent properties and send the preferences to
   * Google Tag Manager.
   */
  setConsent(consent) {
    const consentPreferences =
      CookieConsentUtils.mapConsentPropertiesToPreferences(consent);

    if (window.gtag) {
      window.gtag('consent', 'update', consentPreferences);
    } else {
      console.warn('Google tag manager not found.');
    }

    // Store and publish new consent preferences
    cookieConsent.set(consentPreferences);

    this.loadVendors();
  }

  /**
   * @description
   * Set the default consent preferences if the user has not
   * chosen their preferences.
   *
   * @deprecated
   * Default consent is loaded prior to boostrapping the application.
   */
  setDefaultConsent() {
    let consentPreferences = this.getConsent();
    if (!consentPreferences || consentPreferences === null) {
      consentPreferences = this.getDefaultConsentPreferences();
    }

    // Publish default consent preferences
    console.info('DEFAULT_COOKIE_CONSENT', consentPreferences);
    if (window.gtag) {
      window.gtag('consent', 'default', consentPreferences);
    }
  }

  /**
   * @description
   * Open the consent modal to get the user consent choices
   * for storage.
   */
  openConsentModal() {
    throw new Error('Not implemented in Vue');
  }

  /**
   * @description
   * Load the vendor scripts gated by consent when user consent
   * has been provided.
   */
  loadVendors() {
    const consentPreferences = this.getConsent();
    if (
      consentPreferences?.analytics_storage === 'granted' ||
      consentPreferences?.functionality_storage === 'granted'
    ) {
      // ENG-189: Hubspot script was removed to prevent contact creation
      // TODO: Add vendor script loading here, if required
    }
  }

  /**
   * @description
   * Unload the vendor scripts when user consent has been
   * revoked, or when the user no longer should be tracked.
   */
  unloadVendors() {
    // ENG-189: Hubspot script was removed to prevent contact creation
    // TODO: Add vendor script unloading here, if required
  }

  /**
   * @description
   * Reload the vendor scripts when user consent has been
   * updated, and the user should be tracked.
   */
  reloadVendors() {
    this.unloadVendors();
    this.loadVendors();
  }

  /**
   * @description
   * Manage the legacy consent by accepting all when found
   * and removing the legacy cookie.
   */
  manageLegacyConsent() {
    if (legacyCookieConsent.get()) {
      this.getAcceptAll();
      legacyCookieConsent.erase();
    }
  }
}

export default new CookieService();
